"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  attachExternalSipNumberToCallFlowAction,
  deleteExternalSipPhoneNumberAction,
  detachExternalSipNumberFromCallFlowAction,
  importSipPhoneNumberAction,
  updateExternalSipPhoneNumberAction,
} from "@/server-actions/sip-phone-numbers"
import { withQueryHandling } from "@/lib/with-query-handling"
import { pbxPhoneNumbersQueryKey } from "@/hooks/queries/pbx-phone-numbers"
import { outgoingVoiceCallPhoneNumbersQueryKey } from "@/hooks/queries/outgoing-voice-calls"
import type { ImportSipPhoneNumberInput } from "@/lib/voice/sip-phone-number-validation"
import type { ImportedSipPhoneNumberResult } from "@/lib/voice/sip-phone-numbers"

type ExternalSipCallFlowAssignmentResult = {
  phoneNumber: string
  callFlowId: string | null
}

type ImportSipPhoneNumberMutationInput = {
  input: ImportSipPhoneNumberInput
  setErrors?: (errors: Record<string, unknown>) => void
}

type UpdateSipPhoneNumberMutationInput = {
  phoneNumber: string
  input: ImportSipPhoneNumberInput
  setErrors?: (errors: Record<string, unknown>) => void
}

export function useImportSipPhoneNumberMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      input,
      setErrors,
    }: ImportSipPhoneNumberMutationInput): Promise<ImportedSipPhoneNumberResult> => {
      const result = await withQueryHandling(importSipPhoneNumberAction(input), {
        loading: "Importing SIP phone number...",
        success: "SIP phone number imported",
        setErrors,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: pbxPhoneNumbersQueryKey }),
        queryClient.invalidateQueries({ queryKey: outgoingVoiceCallPhoneNumbersQueryKey }),
      ])
    },
  })
}

export function useUpdateExternalSipPhoneNumberMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      phoneNumber,
      input,
      setErrors,
    }: UpdateSipPhoneNumberMutationInput): Promise<ImportedSipPhoneNumberResult> => {
      const result = await withQueryHandling(updateExternalSipPhoneNumberAction({ phoneNumber, input }), {
        loading: "Updating external SIP phone number...",
        success: "External SIP phone number updated",
        setErrors,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: pbxPhoneNumbersQueryKey }),
        queryClient.invalidateQueries({ queryKey: outgoingVoiceCallPhoneNumbersQueryKey }),
      ])
    },
  })
}

export function useDeleteExternalSipPhoneNumberMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { phoneNumber: string }): Promise<void> => {
      const result = await withQueryHandling(deleteExternalSipPhoneNumberAction(input), {
        loading: "Deleting external SIP phone number...",
        success: "External SIP phone number deleted",
      })
      if ("status" in result) throw new Error(result.errorMessage)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: pbxPhoneNumbersQueryKey }),
        queryClient.invalidateQueries({ queryKey: outgoingVoiceCallPhoneNumbersQueryKey }),
      ])
    },
  })
}

export function useAttachExternalSipNumberToCallFlowMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      phoneNumber: string
      callFlowId: string
    }): Promise<ExternalSipCallFlowAssignmentResult> => {
      const result = await withQueryHandling(attachExternalSipNumberToCallFlowAction(input), {
        loading: "Attaching external SIP number...",
        success: "External SIP number attached",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pbxPhoneNumbersQueryKey })
    },
  })
}

export function useDetachExternalSipNumberFromCallFlowMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { phoneNumber: string }): Promise<ExternalSipCallFlowAssignmentResult> => {
      const result = await withQueryHandling(detachExternalSipNumberFromCallFlowAction(input), {
        loading: "Detaching external SIP number...",
        success: "External SIP number detached",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pbxPhoneNumbersQueryKey })
    },
  })
}
